/**
 * PlatformFeeConfigPage
 * SESMT page for managing platform fee tiers and generating platform invoices
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Send,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { platformFeeService, PlatformFee, PlatformFeeFormData } from '../../services/platformFeeService';
import { invoiceService } from '../../services/invoiceService';
import { useAcao } from '../../authorization/useVeredito';
import { ActionButton } from '../../components/common';
import { formatCurrency, getAvailableMonths, getAvailableYears } from '../../utils/financialHelpers';

const columns: DataTableColumn<PlatformFee>[] = [
  {
    id: 'minLives',
    label: 'Mínimo de Vidas',
    primary: true,
    render: (fee) => fee.minLives.toLocaleString('pt-BR'),
  },
  {
    id: 'maxLives',
    label: 'Máximo de Vidas',
    render: (fee) =>
      fee.maxLives !== null ? fee.maxLives.toLocaleString('pt-BR') : 'Ilimitado',
  },
  {
    id: 'pricePerLife',
    label: 'Preço por Vida',
    align: 'right',
    render: (fee) => formatCurrency(fee.pricePerLife),
  },
  {
    id: 'isActive',
    label: 'Status',
    render: (fee) => (
      <Chip
        label={fee.isActive ? 'Ativo' : 'Inativo'}
        color={fee.isActive ? 'success' : 'default'}
        size="small"
      />
    ),
  },
];

// ============================================================================
// FEE FORM DIALOG
// ============================================================================

interface FeeFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PlatformFeeFormData) => Promise<void>;
  editingFee: PlatformFee | null;
}

const FeeFormDialog: React.FC<FeeFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  editingFee,
}) => {
  const [minLives, setMinLives] = useState('');
  const [maxLives, setMaxLives] = useState('');
  const [pricePerLife, setPricePerLife] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editingFee) {
      setMinLives(editingFee.minLives.toString());
      setMaxLives(editingFee.maxLives !== null ? editingFee.maxLives.toString() : '');
      setPricePerLife(editingFee.pricePerLife.toString());
      setIsActive(editingFee.isActive);
    } else {
      setMinLives('');
      setMaxLives('');
      setPricePerLife('');
      setIsActive(true);
    }
  }, [editingFee, open]);

  const handleSubmit = async () => {
    const data: PlatformFeeFormData = {
      minLives: Number(minLives),
      maxLives: maxLives ? Number(maxLives) : null,
      pricePerLife: Number(pricePerLife),
      isActive,
    };

    setSubmitting(true);
    try {
      await onSubmit(data);
      onClose();
    } catch {
      // error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = minLives !== '' && pricePerLife !== '' && Number(pricePerLife) > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingFee ? 'Editar Faixa de Preço' : 'Nova Faixa de Preço'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Mínimo de Vidas"
            type="number"
            value={minLives}
            onChange={(e) => setMinLives(e.target.value)}
            required
            fullWidth
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Máximo de Vidas (vazio = ilimitado)"
            type="number"
            value={maxLives}
            onChange={(e) => setMaxLives(e.target.value)}
            fullWidth
            inputProps={{ min: 0 }}
            helperText="Deixe vazio para faixa sem limite superior"
          />
          <TextField
            label="Preço por Vida (R$)"
            type="number"
            value={pricePerLife}
            onChange={(e) => setPricePerLife(e.target.value)}
            required
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="Ativo"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || submitting}
        >
          {submitting ? <CircularProgress size={20} /> : editingFee ? 'Salvar' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// GENERATE DIALOG
// ============================================================================

interface GenerateDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (month: number, year: number) => Promise<void>;
}

const GenerateDialog: React.FC<GenerateDialogProps> = ({
  open,
  onClose,
  onGenerate,
}) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [submitting, setSubmitting] = useState(false);

  const handleGenerate = async () => {
    setSubmitting(true);
    try {
      await onGenerate(month, year);
      onClose();
    } catch {
      // error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Gerar Faturas do Mês</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Mes</InputLabel>
            <Select
              value={month}
              onChange={(e) => setMonth(e.target.value as number)}
              label="Mes"
            >
              {getAvailableMonths().map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Ano</InputLabel>
            <Select
              value={year}
              onChange={(e) => setYear(e.target.value as number)}
              label="Ano"
            >
              {getAvailableYears().map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          color="primary"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <Send />}
        >
          Gerar Faturas
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PlatformFeeConfigPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const editar = useAcao('platform-fees.manage', 'Editar');
  const excluir = useAcao('platform-fees.manage', 'Excluir');

  // Data states
  const [fees, setFees] = useState<PlatformFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [editingFee, setEditingFee] = useState<PlatformFee | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await platformFeeService.list();
      setFees(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar faixas de preço';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenCreate = () => {
    setEditingFee(null);
    setFeeDialogOpen(true);
  };

  const handleOpenEdit = (fee: PlatformFee) => {
    setEditingFee(fee);
    setFeeDialogOpen(true);
  };

  const handleFeeSubmit = async (data: PlatformFeeFormData) => {
    try {
      if (editingFee) {
        await platformFeeService.update(editingFee.id, data);
        enqueueSnackbar('Faixa de preço atualizada com sucesso!', { variant: 'success' });
      } else {
        await platformFeeService.create(data);
        enqueueSnackbar('Faixa de preço criada com sucesso!', { variant: 'success' });
      }
      loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar faixa de preço';
      enqueueSnackbar(message, { variant: 'error' });
      throw err;
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta faixa de preço?')) return;

    try {
      await platformFeeService.remove(id);
      enqueueSnackbar('Faixa de preço excluída com sucesso!', { variant: 'success' });
      loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao excluir faixa de preço';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleGenerate = async (month: number, year: number) => {
    try {
      const result = await invoiceService.generatePlatformInvoices(month, year);
      if (result.failed && result.failed.length > 0) {
        const failedNames = result.failed.map(
          (f: { companyName: string; error: string }) => `${f.companyName}: ${f.error}`
        ).join('\n');
        enqueueSnackbar(
          `${result.generated} fatura(s) gerada(s), mas ${result.failed.length} falharam:\n${failedNames}`,
          { variant: 'warning', style: { whiteSpace: 'pre-line' } },
        );
      } else {
        enqueueSnackbar(
          `${result.generated} fatura(s) de plataforma gerada(s) com sucesso!`,
          { variant: 'success' },
        );
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar faturas de plataforma';
      enqueueSnackbar(message, { variant: 'error' });
      throw err;
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/home' },
        { label: 'Financeiro', path: '/financial' },
        { label: 'Taxas de Plataforma' },
      ]}
      title="Taxas de Plataforma"
      actions={
        <Tooltip title="Atualizar lista">
          <span>
            <IconButton onClick={loadData} disabled={loading}>
              <Refresh />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <ActionButton
            acao="platform-fees.manage"
            rotulo="Nova Faixa de Preço"
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
          >
            Nova Faixa de Preço
          </ActionButton>
          <ActionButton
            acao="platform-fees.manage"
            rotulo="Gerar Faturas do Mês"
            variant="outlined"
            startIcon={<Send />}
            onClick={() => setGenerateDialogOpen(true)}
          >
            Gerar Faturas do Mês
          </ActionButton>
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

      <DataTable
        columns={columns}
        rows={fees}
        getRowId={(fee) => fee.id}
        loading={loading}
        emptyState={{
          title: 'Nenhuma faixa de preço cadastrada ainda.',
          description:
            'As faixas são definidas nesta tela e determinam quanto a plataforma cobra por atendimento.',
        }}
        actions={[
          {
            icon: <Edit fontSize="small" />,
            onClick: handleOpenEdit,
            ...editar,
          },
          {
            icon: <Delete fontSize="small" />,
            color: 'error',
            onClick: (fee) => handleDelete(fee.id),
            ...excluir,
          },
        ]}
      />

        {/* Dialogs */}
        <FeeFormDialog
          open={feeDialogOpen}
          onClose={() => setFeeDialogOpen(false)}
          onSubmit={handleFeeSubmit}
          editingFee={editingFee}
        />

        <GenerateDialog
          open={generateDialogOpen}
          onClose={() => setGenerateDialogOpen(false)}
          onGenerate={handleGenerate}
        />
    </PageShell>
  );
};

export default PlatformFeeConfigPage;
